import { ReactNode, useContext, useEffect, useState } from 'react'
import Instruments from './Instruments/Instruments'
import Section from '../../components/Section'
import MasterVolume from './Components/MasterVolume'
import MenuWithContent from '../../assets/components/MenuWithContent'
import { useCurrentSong } from '../../hooks/contexts/currentSongContext'
import { useChangeCurrentLocation } from '../../hooks/contexts/currentLocationContext'
import { Box, Container, SwipeableDrawer } from '@mui/material'
import Text from '../../assets/components/Text'
import BasicButton from '../../assets/components/BasicButton'
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen'
import AudioPlayer from '../../components/audioplayer/AudioPlayer'
import TuneIcon from '@mui/icons-material/Tune'
import { useConnectedInstruments } from '../../hooks/contexts/connectedInstrumentsContext'
import { PolyglotContext } from '../../polyglot/polyGlotContext'

const Welcome = () => {
    const { polyglot } = useContext(PolyglotContext)
    const currentSong = useCurrentSong()
    const changeCurrentLocation = useChangeCurrentLocation()
    const isSmallScreen = useIsSmallScreen()
    const connectedInstruments = useConnectedInstruments()
    const hasConnectedInstruments = connectedInstruments && connectedInstruments.length > 0

    const [isOpen, setIsOpen] = useState(false)

    const toggleDrawer = () => {
        setIsOpen(!isOpen)
    }

    useEffect(() => {
        changeCurrentLocation(polyglot.t('components.navbar.homeRouteBtn'))
    }, [changeCurrentLocation])

    const [instrumentVolumes, setInstrumentVolumes] = useState<{
        [key: string]: number
    }>({})

    return (
        <MenuWithContent
            showCurrentlyPlaying
            menuItems={<></>}
            content={
                <Container
                    sx={{
                        paddingTop: isSmallScreen ? 0 : '2rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        height: '100%',
                        minWidth: '100%'
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: isSmallScreen ? '1fr' : '8fr 1fr',
                            width: '100%',
                            height: '80vh'
                        }}
                    >
                        <Instruments currentSong={currentSong} instrumentVolumes={instrumentVolumes} setInstrumentVolumes={setInstrumentVolumes} connectedInstruments={connectedInstruments} />
                        {!isSmallScreen && hasConnectedInstruments && <MasterVolume instrumentVolumes={instrumentVolumes} setInstrumentVolumes={setInstrumentVolumes} isSmallScreen={isSmallScreen} />}
                    </Box>
                    {isSmallScreen ? (
                        <Box sx={{ display: 'flex', position: 'absolute', bottom: 70 }}>
                            <BasicButton fullWidth={isSmallScreen} title="Mixer" onClick={toggleDrawer} icon={TuneIcon} variant="contained" />
                            <Drawer isOpen={isOpen} toggleDrawer={toggleDrawer} heading="Mixer">
                                <MasterVolume instrumentVolumes={instrumentVolumes} setInstrumentVolumes={setInstrumentVolumes} isSmallScreen={isSmallScreen} />
                                {currentSong?.hasSections && <Section />}
                                {currentSong?.backingTrack && <AudioPlayer backingTrack={currentSong.backingTrack} isSmallScreen={isSmallScreen} />}
                            </Drawer>
                        </Box>
                    ) : (
                        <Box sx={{ position: 'absolute', bottom: 15, alignitems: 'center', paddingLeft: 5, width: '85%' }}>{currentSong?.backingTrack && <AudioPlayer backingTrack={currentSong.backingTrack} />}</Box>
                    )}
                </Container>
            }
        />
    )
}

interface IDrawer {
    isOpen: boolean
    toggleDrawer: () => void
    children: ReactNode
    heading?: string
    variant?: 'bottom' | 'left' | 'right' | 'top' | undefined
}

export const Drawer = ({ isOpen, toggleDrawer, children, heading, variant = 'bottom' }: IDrawer) => {
    return (
        <SwipeableDrawer
            anchor={variant}
            open={isOpen}
            onClose={toggleDrawer}
            onOpen={toggleDrawer}
            sx={{
                '& .MuiDrawer-paper': {
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    height: '95%',
                    width: {
                        xs: '100%',
                        sm: '70%',
                        md: '50%',
                        lg: '40%'
                    },
                    margin: '0 auto' // Centers the drawer
                }
            }}
        >
            <Box sx={{ height: 'auto' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingY: '2rem'
                    }}
                >
                    {heading && <Text size="heading">{heading}</Text>}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 5,
                        pb: 5,
                        px: 5
                    }}
                >
                    {children}
                </Box>
            </Box>
        </SwipeableDrawer>
    )
}

export default Welcome
